import React from "react";
import styles from "./FHIRPatient.module.scss";
import _ from "lodash";
import DateTimePicker from "react-datetime-picker";
import { capitalize } from "../../utils/textFunctions";
import RecordOverrideOptions from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideOptions";
import {
  overrideSummaryForAddress,
  listOfAddressTypes
} from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideUtil";
import { handleMultipleAttributeOverride } from "../../routes/NewSearchRecords/SearchRecordTable/MultipleAttributeOverride/Utils";
import { formattedDateWithDefault } from "../../utils/formattedDate";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import Button from "../../components/Button";
import LinkButton from "../../components/LinkButton";
import OverrideAuditInputModal from "../../routes/NewSearchRecords/SearchRecordTable/OverrideAuditInputModal";
import { renderFieldWithDiff } from "./Utils";

const Address = ({
  data,
  summary: isSummary,
  className,
  type,
  oncRecordOverrideIconClick,
  isInvisible = false,
  isAggregateInfo = false,
  isImdm = false,
  isNewQuarantine = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides,
  hasDiff = false,
  diffInfo = null,
  isPreviewMode = false
}) => {
  const [
    replaceRecordOverrideDialog,
    setReplaceRecordOverrideDialog
  ] = React.useState(false);
  const [
    recordOverrideDialogContent,
    setRecordOverrideDialogContent
  ] = React.useState({});
  const [addressLine, setAddressLine] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [district, setDistrict] = React.useState("");
  const [postalCode, setPostalCode] = React.useState("");
  const [valueChanged, setValueChanged] = React.useState(false);
  const [valueCopied, setValueCopied] = React.useState(false);
  const [sourceTimeStamp, setSourceTimeStamp] = React.useState(new Date());
  const [addressStartDate, setAddressStartDate] = React.useState(new Date());
  const [addressEndDate, setAddressEndDate] = React.useState(new Date());
  const [copiedAddress, setCopiedAddress] = React.useState("");
  const [addressType, setAddressType] = React.useState("PERPRIMERRES|home");
  const [uniqueAddressList, setUniqueAddressList] = React.useState(false);
  const [showAuditModal, setShowAuditModal] = React.useState(false);
  const [pendingConfirmDetails, setPendingConfirmDetails] = React.useState(
    null
  );

  if (isSummary && data.length === 0) {
    return <div>Address: [Empty]</div>;
  }
  if (isSummary) {
    const address = data[0];
    const addressDisplay = address.line ? address.line.join("\n") : "";

    // If in preview mode and there's a diff, show both prior and new values (only for use === "home")
    if (isPreviewMode && hasDiff && diffInfo && address.use === "home") {
      const priorAddress =
        diffInfo.prior && diffInfo.prior[0] && diffInfo.prior[0].line
          ? diffInfo.prior[0].line.join("\n")
          : "";

      return (
        <div>
          Address:
          {diffInfo.changeType === "removed" ? (
            <span className="preview-diff-prior"> {priorAddress}</span>
          ) : diffInfo.changeType === "added" ? (
            <span className="preview-diff-highlight"> {addressDisplay}</span>
          ) : (
            <>
              <span className="preview-diff-prior"> {priorAddress}</span>
              {" → "}
              <span className="preview-diff-highlight"> {addressDisplay}</span>
            </>
          )}
        </div>
      );
    }

    return address.line ? <div>Address: {addressDisplay}</div> : null;
  }

  const prepareOverrideOriginalValue = (address, type, summaryType) => {
    const extensionMap = overrideSummaryForAddress(address, summaryType, type);
    //console.log("map: ", extensionMap);
    oncRecordOverrideIconClick(extensionMap);
  };

  const actionHandler = detailsMap => {
    if (isMultipleAttributeOverride) {
      handleMultipleAttributeOverride(
        detailsMap,
        "address",
        attributeOverrides,
        setAttributeOverrides
      );
      setReplaceRecordOverrideDialog(false);
    } else {
      oncRecordOverrideIconClick(detailsMap);
    }
  };

  const handleUniqueAddress = () => {
    return data.reduce((finalArray, current) => {
      if (
        current &&
        Object.keys(current).length === 1 &&
        current.hasOwnProperty("extension")
      ) {
        return finalArray;
      }
      let obj = finalArray.find(
        item =>
          JSON.stringify(item.line) === JSON.stringify(current.line) &&
          item.city === current.city &&
          item.district === current.district &&
          item.state === current.state &&
          item.country === current.country &&
          item.postalCode === current.postalCode
      );
      if (obj) {
        return finalArray;
      }
      return finalArray.concat([current]);
    }, []);
  };

  const handleReplaceOverride = detailsMap => {
    if (
      detailsMap["Mark as"] &&
      (detailsMap["Mark as"] === "Replace" || detailsMap["Mark as"] === "New")
    ) {
      if (detailsMap.originalFieldData) {
        setCity(
          detailsMap.originalFieldData.city
            ? detailsMap.originalFieldData.city
            : ""
        );
        setState(
          detailsMap.originalFieldData.state
            ? detailsMap.originalFieldData.state
            : ""
        );
        setPostalCode(
          detailsMap.originalFieldData.postalCode
            ? detailsMap.originalFieldData.postalCode
            : ""
        );
        setAddressLine(
          detailsMap.originalFieldData.line
            ? detailsMap.originalFieldData.line.join("\n")
            : ""
        );
        setCountry(
          detailsMap.originalFieldData.country
            ? detailsMap.originalFieldData.country
            : ""
        );
        setDistrict(
          detailsMap.originalFieldData.district
            ? detailsMap.originalFieldData.district
            : ""
        );
        setSourceTimeStamp(
          detailsMap["Source Timestamp"] &&
            detailsMap["Source Timestamp"] !== "-"
            ? new Date(detailsMap["Source Timestamp"])
            : new Date()
        );
        setAddressStartDate(
          detailsMap["Address Start Date"] &&
            detailsMap["Address Start Date"] !== "-"
            ? new Date(detailsMap["Address Start Date"])
            : new Date()
        );
        setAddressEndDate(
          detailsMap["Address End Date"] &&
            detailsMap["Address End Date"] !== "-"
            ? new Date(detailsMap["Address End Date"])
            : new Date()
        );
      }
      setUniqueAddressList(handleUniqueAddress());
      setCopiedAddress(detailsMap);
      setRecordOverrideDialogContent(detailsMap);
      setReplaceRecordOverrideDialog(true);
    } else {
      actionHandler(detailsMap);
    }
  };

  const handleConfirm = () => {
    const detailsMap = { ...recordOverrideDialogContent };
    detailsMap.originalFieldData.city = city;
    detailsMap.originalFieldData.state = state;
    detailsMap.originalFieldData.postalCode = postalCode;
    detailsMap.originalFieldData.district = district;
    detailsMap.originalFieldData.country = country;
    detailsMap.originalFieldData.line = addressLine.split("\n");
    if (!detailsMap.originalFieldData.extension) {
      detailsMap.originalFieldData.extension = [];
    }
    const srTmStmpExt = detailsMap.originalFieldData.extension.filter(
      item =>
        item.url ===
        "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/imdm/#address-srcTmStmp"
    );
    if (srTmStmpExt.length > 0) {
      srTmStmpExt[0].valueDateTime = sourceTimeStamp.toISOString();
    } else {
      detailsMap.originalFieldData.extension.push({
        url:
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/imdm/#address-srcTmStmp",
        valueDateTime: sourceTimeStamp.toISOString()
      });
    }
    if (!detailsMap.originalFieldData.period) {
      detailsMap.originalFieldData.period = {};
    }
    detailsMap.originalFieldData.period.start = addressStartDate.toISOString();
    detailsMap.originalFieldData.period.end = addressEndDate.toISOString();

    if (detailsMap["Mark as"] === "New") {
      detailsMap.originalFieldData.use = addressType.split("|")[1];
      detailsMap.originalFieldData.extension.push({
        url:
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/imdm/#attrCode",
        valueString: addressType.split("|")[0]
      });
    }
    if (isMultipleAttributeOverride)
      // Directly proceed to next step without audit modal
      actionHandler(detailsMap);
    else {
      setPendingConfirmDetails(detailsMap);
      setShowAuditModal(true);
    }
  };

  const handleAuditSubmit = auditInfo => {
    if (pendingConfirmDetails) {
      const updatedDetails = {
        ...pendingConfirmDetails,
        auditInfoList: auditInfo
      };
      actionHandler(updatedDetails);
      setPendingConfirmDetails(null);
    }
    setShowAuditModal(false);
  };

  const handleCopiedData = () => {
    setAddressLine(copiedAddress.line ? copiedAddress.line.join("\n") : "");
    setCity(copiedAddress.city ? copiedAddress.city : "");
    setDistrict(copiedAddress.district ? copiedAddress.district : "");
    setState(copiedAddress.state ? copiedAddress.state : "");
    setCountry(copiedAddress.country ? copiedAddress.country : "");
    setPostalCode(copiedAddress.postalCode ? copiedAddress.postalCode : "");
  };

  const replaceOverrideModal = () => {
    return (
      <Modal
        open={replaceRecordOverrideDialog}
        onClose={() => setReplaceRecordOverrideDialog(false)}
        overflow={{ overflow: "scroll" }}
        maxheight={{ "max-height": "90%" }}
      >
        <h3 style={{ textAlign: "center" }}>
          {recordOverrideDialogContent["Mark as"] !== "New" ? "Replace" : "New"}{" "}
          override
        </h3>
        <br />
        {recordOverrideDialogContent && (
          <table style={{ borderSpacing: "6px" }}>
            <td>
              <table className={"VerticalTable"}>
                <tr>
                  <th>Address Line</th>
                  <td>
                    <input
                      type="text"
                      value={addressLine}
                      onChange={e => {
                        setAddressLine(e.target.value);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th>District</th>
                  <td>
                    <input
                      type="text"
                      value={district}
                      onChange={e => {
                        setDistrict(e.target.value);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Country</th>
                  <td>
                    <input
                      type="text"
                      value={country}
                      onChange={e => {
                        setCountry(e.target.value);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>
                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <tr>
                    <th>Use</th>
                    <td>
                      <input
                        type="text"
                        value={recordOverrideDialogContent["Use"]}
                        disabled={true}
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <th>Address Start Date</th>
                  <td>
                    <DateTimePicker
                      format="MM-dd-y hh:mm:ss a"
                      value={addressStartDate}
                      onChange={changeDate => {
                        setAddressStartDate(changeDate);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Address End Date</th>
                  <td>
                    <DateTimePicker
                      format="MM-dd-y hh:mm:ss a"
                      value={addressEndDate}
                      onChange={changeDate => {
                        setAddressEndDate(changeDate);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>
              </table>
            </td>
            <td>
              <table className={"VerticalTable"}>
                <tr>
                  <th>City</th>
                  <td>
                    <input
                      type="text"
                      value={city}
                      onChange={e => {
                        setCity(e.target.value);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>

                <tr>
                  <th>State</th>
                  <td>
                    <input
                      type="text"
                      value={state}
                      onChange={e => {
                        setState(e.target.value);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>

                <tr>
                  <th>Postal Code</th>
                  <td>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={e => {
                        setPostalCode(e.target.value);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>

                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <tr>
                    <th>Attribute</th>
                    <td>
                      <input
                        type="text"
                        value={recordOverrideDialogContent["Attribute"]}
                        disabled={true}
                      />
                    </td>
                  </tr>
                )}

                <tr>
                  <th>Source Timestamp</th>
                  <td>
                    <DateTimePicker
                      format="MM-dd-y hh:mm:ss a"
                      value={sourceTimeStamp}
                      onChange={changeDate => {
                        setSourceTimeStamp(changeDate);
                        if (!valueChanged) {
                          setValueChanged(true);
                        }
                      }}
                    />
                  </td>
                </tr>
                <tr></tr>
                <tr>
                  {recordOverrideDialogContent["Mark as"] === "New" && (
                    <th>Address Type</th>
                  )}
                  {recordOverrideDialogContent["Mark as"] === "New" && (
                    <td>
                      <select
                        value={addressType}
                        onChange={e => setAddressType(e.target.value)}
                      >
                        {listOfAddressTypes().map(addrType => {
                          return (
                            <option
                              value={addrType.attrCode + "|" + addrType.use}
                            >
                              {addrType.displayName}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                  )}
                </tr>
              </table>
            </td>
          </table>
        )}

        <br />
        <br />
        <div className={"copiedAddress"}>
          <table>
            <thead>
              <tr className={"recOvrTable"}>
                <th></th>
                <th>Address Line</th>
                <th>City</th>
                <th>District</th>
                <th>State</th>
                <th>Country</th>
                <th>Postal Code</th>
                <th>Source Timestamp</th>
              </tr>
            </thead>

            {uniqueAddressList &&
              uniqueAddressList.slice(0, 24).map((address, index) => {
                return (
                  <tbody>
                    <tr key={"key" + index} className={"recOvrTable"}>
                      <td>
                        <input
                          type="radio"
                          name="address"
                          value="copiedAddress"
                          onChange={e => {
                            setCopiedAddress(address, index);
                            if (!valueCopied) {
                              setValueCopied(true);
                            }
                          }}
                        />
                      </td>

                      <td>{address.line}</td>

                      <td>{address.city}</td>

                      <td>{address.district}</td>

                      <td>{address.state}</td>

                      <td>{address.country}</td>

                      <td>{address.postalCode}</td>

                      <td>{address.sourceTimestamp}</td>
                    </tr>
                  </tbody>
                );
              })}
          </table>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Button disabled={!valueCopied} onClick={() => handleCopiedData()}>
              Copy Address
            </Button>
            <Button
              primary
              disabled={!valueChanged && !valueCopied}
              onClick={() => handleConfirm()}
            >
              Confirm Changes
            </Button>
            <Button
              onClick={() => {
                setValueChanged(false);
                setValueCopied(false);
                setReplaceRecordOverrideDialog(false);
              }}
            >
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  const addNewAddressLink = () => {
    return (
      <LinkButton
        stickLeft={true}
        icon={"add"}
        onClick={() =>
          handleReplaceOverride({
            Field:
              type === "birthplaceAddress" ? "Birth Place Address" : "Address",
            originalFieldData: {},
            "Mark as": "New",
            originalFieldName: "address"
          })
        }
        iconClassName={styles.addNewOverrodeIcon}
      >
        {"Add Address"}
      </LinkButton>
    );
  };

  const UndeliverableInd = undelRsnTpCdExt => {
    switch (undelRsnTpCdExt.valueString) {
      case "5":
        return "5-Invalid Address";
      case "8":
        return "8-Deleted Address";
      default:
        return "-";
    }
  };

  if (!isSummary && data.length === 0) {
    return (
      <section>
        {replaceOverrideModal()}
        {isImdm &&
          oncRecordOverrideIconClick &&
          !isInvisible &&
          !isNewQuarantine &&
          !isMultipleAttributeOverride &&
          addNewAddressLink()}

        {!oncRecordOverrideIconClick && (
          <>
            <h3>Addresses</h3>
            <div>
              <h4>Empty</h4>
            </div>
          </>
        )}
      </section>
    );
  }

  return (
    <>
      {showAuditModal && (
        <OverrideAuditInputModal
          isOpen={showAuditModal}
          onCancel={() => setShowAuditModal(false)}
          onSubmit={handleAuditSubmit}
        />
      )}
      <section>
        <div className={className + " " + styles.header}>
          {!isAggregateInfo && (
            <div
              className={
                isMultipleAttributeOverride
                  ? styles.multipleAttributeOverrideColumn
                  : styles.actionColumn
              }
            />
          )}
          <div>
            <h4>Address Line</h4>
          </div>
          <div>
            <h4>City</h4>
          </div>
          <div>
            <h4>District</h4>
          </div>
          <div>
            <h4>State</h4>
          </div>
          <div>
            <h4>Country</h4>
          </div>
          <div>
            <h4>Postal Code</h4>
          </div>
          <div>
            <h4>Use</h4>
          </div>
          <div>
            <h4>Attribute</h4>
          </div>
          <div>
            <h4>Source Timestamp</h4>
          </div>
          {isAggregateInfo && (
            <div>
              <h4>Source</h4>
            </div>
          )}
          <div
            style={{
              paddingLeft: 15
            }}
          >
            <h4>Undeliverable</h4>
          </div>
        </div>

        {data.map((address, index) => {
          const attrCode = address.extension
            ? address.extension.filter(e => e.url.includes("attrCode"))
            : "";
          const sourceCode = address.extension
            ? address.extension.filter(e => e.url.includes("sourceCode"))
            : "";
          const srcTmStmpExt = address.extension
            ? address.extension.filter(e =>
                e.url.includes("#address-srcTmStmp")
              )
            : "";
          const undelRsnTpCdExt = address.extension
            ? address.extension.filter(e =>
                e.url.includes("#address-undelRsnTpCd")
              )
            : "";

          // Get prior address data if available for diff comparison (only for use === "home")
          const isHomeAddress = address.use === "home";
          const priorAddress =
            isPreviewMode &&
            hasDiff &&
            diffInfo &&
            diffInfo.prior &&
            diffInfo.prior[index] &&
            isHomeAddress
              ? diffInfo.prior[index]
              : null;
          const changeType =
            diffInfo && isHomeAddress ? diffInfo.changeType : null;

          const addrMap = {
            Field:
              type === "birthplaceAddress" ? "Birth Place Address" : "Address",
            "Address Line": address.line
              ? address.line.map((addressLine, lKey) => {
                  return <div key={lKey}>{addressLine}</div>;
                })
              : "NA",
            City: address.city ? address.city : "-",
            District: address.district ? address.district : "-",
            State: address.state ? address.state : "-",
            Country: address.country ? address.country : "-",
            "Postal Code": address.postalCode ? address.postalCode : "-",
            Use: address.use ? capitalize(address.use) : "-",
            Attribute: attrCode[0] ? attrCode[0].valueString : "-",
            Source: sourceCode[0] ? sourceCode[0].valueString : "-",
            Undeliverable: undelRsnTpCdExt[0]
              ? UndeliverableInd(undelRsnTpCdExt[0])
              : "-",
            "Source Timestamp": srcTmStmpExt[0]
              ? formattedDateWithDefault(srcTmStmpExt[0].valueDateTime)
              : "-",
            "Address Start Date":
              address.period && address.period.start
                ? formattedDateWithDefault(address.period.start)
                : "-",
            "Address End Date":
              address.period && address.period.end
                ? formattedDateWithDefault(address.period.end)
                : "-",
            "Mark as": "",
            originalFieldName: "address",
            originalFieldData: address,
            originalFieldDataCopy: JSON.parse(JSON.stringify(address)),
            id: index
          };

          // Prepare prior values for diff display
          const priorAddrMap = priorAddress
            ? {
                "Address Line": priorAddress.line
                  ? priorAddress.line.map((addressLine, lKey) => {
                      return <div key={lKey}>{addressLine}</div>;
                    })
                  : "NA",
                City: priorAddress.city ? priorAddress.city : "-",
                District: priorAddress.district ? priorAddress.district : "-",
                State: priorAddress.state ? priorAddress.state : "-",
                Country: priorAddress.country ? priorAddress.country : "-",
                "Postal Code": priorAddress.postalCode
                  ? priorAddress.postalCode
                  : "-",
                Use: priorAddress.use ? capitalize(priorAddress.use) : "-"
              }
            : null;

          return (
            <div
              key={index}
              className={`${className} ${
                hasDiff && isPreviewMode && isHomeAddress
                  ? "preview-diff-highlight"
                  : ""
              }`}
            >
              {!isAggregateInfo && (
                <div
                  className={
                    isMultipleAttributeOverride
                      ? styles.multipleAttributeOverrideColumn
                      : styles.actionColumn
                  }
                  style={{ zIndex: 998 - index }}
                >
                  {oncRecordOverrideIconClick &&
                    !isInvisible &&
                    !isNewQuarantine && (
                      <RecordOverrideOptions
                        detailsMap={addrMap}
                        onClickRecordOverrideAction={detailsMap =>
                          handleReplaceOverride(detailsMap)
                        }
                        onClickRecordOverrideButton={summaryType =>
                          prepareOverrideOriginalValue(
                            addrMap.originalFieldData,
                            type,
                            summaryType
                          )
                        }
                        extension={
                          address.hasOwnProperty("extension")
                            ? address.extension.filter(
                                item =>
                                  item.url ===
                                  "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override"
                              )
                            : []
                        }
                        isMultipleAttributeOverride={
                          isMultipleAttributeOverride
                        }
                        selectedOverrides={attributeOverrides.address}
                      />
                    )}
                </div>
              )}
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["Address Line"],
                    priorAddrMap ? priorAddrMap["Address Line"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["Address Line"]}</div>
                )}
              </div>
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["City"],
                    priorAddrMap ? priorAddrMap["City"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["City"]}</div>
                )}
              </div>
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["District"],
                    priorAddrMap ? priorAddrMap["District"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["District"]}</div>
                )}
              </div>
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["State"],
                    priorAddrMap ? priorAddrMap["State"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["State"]}</div>
                )}
              </div>
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["Country"],
                    priorAddrMap ? priorAddrMap["Country"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["Country"]}</div>
                )}
              </div>
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["Postal Code"],
                    priorAddrMap ? priorAddrMap["Postal Code"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["Postal Code"]}</div>
                )}
              </div>
              <div>
                {isHomeAddress && changeType ? (
                  renderFieldWithDiff(
                    addrMap["Use"],
                    priorAddrMap ? priorAddrMap["Use"] : null,
                    changeType,
                    isPreviewMode,
                    hasDiff
                  )
                ) : (
                  <div>{addrMap["Use"]}</div>
                )}
              </div>
              <div>
                <div>{addrMap["Attribute"]}</div>
              </div>
              <div>
                <div>{addrMap["Source Timestamp"]}</div>
              </div>
              {isAggregateInfo && (
                <div>
                  <div>{addrMap["Source"]}</div>
                </div>
              )}
              <div>
                <div
                  style={{
                    paddingLeft: 15
                  }}
                >
                  {addrMap["Undeliverable"]}
                </div>
              </div>
            </div>
          );
        })}
        {isImdm &&
          oncRecordOverrideIconClick &&
          !isInvisible &&
          !isNewQuarantine &&
          !isMultipleAttributeOverride &&
          addNewAddressLink()}
        {replaceRecordOverrideDialog && replaceOverrideModal()}
      </section>
    </>
  );
};

export default Address;
